import { useCallback, useEffect, useState } from 'react';
import type { RefObject } from 'react';

type Options = {
    total: number;
    autoplayMs?: number;
    enableKeyboard?: boolean;
    keyboardScopeRef?: RefObject<HTMLElement | null>;
};

function prefersReducedMotion() {
    return typeof window !== 'undefined'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function useCarousel({ total, autoplayMs, enableKeyboard = false, keyboardScopeRef }: Options) {
    const [index, setIndex] = useState(0);

    const goPrev = useCallback(() => {
        if (total <= 1) { return; }
        setIndex((i) => (i - 1 + total) % total);
    }, [total]);

    const goNext = useCallback(() => {
        if (total <= 1) { return; }
        setIndex((i) => (i + 1) % total);
    }, [total]);

    useEffect(() => {
        if (!enableKeyboard) { return; }
        const onKey = (e: KeyboardEvent) => {
            const scope = keyboardScopeRef?.current;
            if (scope && !scope.contains(document.activeElement)) { return; }
            if (e.key === 'ArrowLeft') { goPrev(); }
            if (e.key === 'ArrowRight') { goNext(); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [goPrev, goNext, enableKeyboard, keyboardScopeRef]);

    useEffect(() => {
        if (!autoplayMs || autoplayMs <= 0 || total <= 1) { return; }
        if (prefersReducedMotion()) { return; }
        const id = window.setInterval(() => {
            setIndex((i) => (i + 1) % total);
        }, autoplayMs);
        return () => window.clearInterval(id);
        // index is intentional: including it resets the interval on every
        // manual prev/next so autoplay pauses for a full autoplayMs after
        // user interaction.
    }, [index, total, autoplayMs]);

    return { index, setIndex, goPrev, goNext };
}
